import { State } from "./State";
import SpinModel from "../model/SpinModel";
import { OverlayType } from "../server/data/ISpinData";
import ViewManager from "../utils/ViewManager";
import { DefaultLayers } from "../utils/Layers";
import ReelGroup from "../views/ReelGroup";
import SoundController from "../utils/SoundController";
import SymbolAnimation from "../views/SymbolAnimation";
import PromiseHelpers from "../utils/PromiseHelpers";
import { Tween } from "../Tween";
import SoundVars from "../const/SoundVars";

export class PlayAnimatedExpand extends State {
    protected enterDelay: number = 0;

    enter() {
        super.enter();

        // Play all expands - then move on
        Tween.delayedCall(this.enterDelay, async () => {
            await this.playExpands();
            this.exit();
        }, [], this);
    }

    private async playExpands(): Promise<void> {
        return new Promise(async (resolve) => {
            let expands = new Array<Promise<void>>();

            // We always add the Expanding types
            expands.push(this.getExpandPromise(OverlayType.Expanding));

            // If we should reuse this flow for Walking types also
            if (this.expandWalkingWilds()) {
                expands.push(this.getExpandPromise(OverlayType.Walking));
            }

            // Then wait for them all to resolve
            await PromiseHelpers.combineParallel(expands);

            // And go back to the exit
            resolve();
        });
    }

    /**
     *  Returns a promise that will execute all expands of a give type
     */
    protected getExpandPromise(overlayType: OverlayType) {
        const promise = new Promise<void>((resolve) => {
            let overlay = SpinModel.getOverlay(overlayType);

            if (overlay) {
                let counter = overlay.initPositions?.length || 0;
                overlay.initPositions?.forEach((position) => {
                    let symbolId = overlay.symbolPosition[position.reel].find(
                        (symbol) => symbol != undefined && symbol != null
                    );

                    if (symbolId) {
                        // hide reel symbol that is behind expanding symbol.
                        // it's necessary if we have moving expand(safari game).
                        let symbol = this.getReelGroup().getSymbolAt(position.reel, position.row);
                        symbol.setId(symbolId);
                        symbol.hide();

                        this.showExpandAt(
                            position.reel,
                            position.row,
                            symbolId
                        ).then(() => {
                            counter--;
                            if (counter === 0) {
                                // Lock the expanded symbols - they will be unlocked when needed
                                this.lockAnimations(
                                    overlay.symbolPosition,
                                    overlayType == OverlayType.Walking
                                );
    
                                // Hide the overlapped symbols
                                this.hideSymbols(overlay.symbolPosition);
                                this.getReelGroup().sortSymbols();
                                // Then exit
                                resolve();
                            }
                        });
                    }
                });
            } else {
                // If there is no such overlay - resolve directly
                resolve();
            }
        });

        return promise;
    }

    /**
     * Check if Walking WILDs should also be expanded - often that would be the case
     */
    protected expandWalkingWilds(): boolean {
        return true;
    }

    protected playWildExpandSound(symbolId: number) {
        SoundController.playSound(SoundVars.WILD_EXPAND, false, undefined, false);
    }

    private showExpandAt(
        reelId: number,
        rowId: number,
        symbolId: number
    ): Promise<boolean> {
        return new Promise<boolean>((res) => {
            // Then play the expand animation and then loop the expanded version
            let sym = this.getReelGroup().getSymbolAt(reelId, rowId);
            let anim = this.getReelGroup().winSymbolsView.getAnimAtReelCell(
                reelId,
                rowId
            );

            // If the symbolAnim is already locked it's either already expanded or it should not be touched
            if (anim.isLocked) {
                // So do nothing
                sym.setId(symbolId);
                res(true);
            } else {
                // Otherwise - play the animations
                this.playWildExpandSound(symbolId);
                anim.showExpand(
                    symbolId,
                    this.getExpandAnimSuffix(reelId, rowId)
                ).then(() => {
                    // Play the loop animation (if present and set)
                    let getLoopName = this.getLoopAnimName(reelId, rowId);

                    // sym.setId(symbolId); // Why do we need this?

                    if (getLoopName) {
                        anim.showAnim(symbolId, getLoopName, true).then(() => {
                            res(true);
                        });
                    } else {
                        res(true);
                    }
                });

                this.applyAnimEffects(anim, reelId, rowId);
            }
        });
    }

    /**
     * Add specific effects on the animation
     * @param anim
     */
     protected applyAnimEffects(anim: SymbolAnimation, reelId: number = -1, rowId: number = -1) {
        // To be extended
    }

    /**
     * Add specific suffix to the animation if needed
     * @param reelId
     * @param rowId
     */
    protected getExpandAnimSuffix(reelId: number, rowId: number) {
        return "";
    }

    /**
     * Retrieve loop animation name
     * @param reelId
     * @param rowId
     */
    protected getLoopAnimName(reelId: number, rowId: number) {
        return "expand_loop";
    }

    private lockAnimations(symPositions: number[][], isWalking: boolean) {
        // Disable animations for the overlapped symbols
        symPositions.forEach((reel, reelIndex) => {
            reel.forEach((sym, rowIndex) => {
                if (sym != undefined) {
                    let anim = this.getReelGroup().winSymbolsView.getAnimAtReelCell(
                        reelIndex,
                        rowIndex
                    );

                    // Block win animations
                    anim.isLocked = true;

                    if (isWalking) {
                        anim.isWalking = true;
                    }
                }
            });
        });
    }

    protected hideSymbols(symPositions: number[][]) {
        // Disable animations for the overlapped symbols
        symPositions.forEach((reel, reelIndex) => {
            let hasExpand: boolean = false;
            reel.forEach((id) => {
                if (id != undefined) {
                    hasExpand = true;
                }
            })
            reel.forEach((sym, rowIndex) => {
                if (sym != undefined) {
                    // Hide symbols and mark them as hidden
                    let symbol = this.getReelGroup().getSymbolAt(
                        reelIndex,
                        rowIndex
                    );
                    symbol.setId(sym);
                    symbol.hide();
                    symbol.forceHidden = true;
                } else if(hasExpand) {
                    let symbol = this.getReelGroup().getSymbolAt(
                        reelIndex,
                        rowIndex
                    );
                    symbol.setId(-1);
                }
            });
        });
    }

    protected getReelGroup() {
        // Get the reel where we are expanding
        const scene = ViewManager.getLayer(
            DefaultLayers.Reels
        ).getCurrentScene();
        return scene.getSubView("ReelGroup") as ReelGroup;
    }
}
